import { css, type Theme } from "@emotion/react";
import { borders } from "aviary-tokens";

import { animations, timing } from "@styles";

const controlBase = (theme: Theme) => css`
  transition: all ${timing.moderate} ${animations.easeOutCubic};
  color: ${theme.system.textBase};

  position: relative;
  vertical-align: top;
  align-items: center;
  display: inline-flex;
  justify-content: flex-start;
  background-color: ${theme.system.backgroundBase};

  font-size: ${theme.aviaryTypography.body.fontSize};
  height: auto;
  line-height: ${theme.aviaryTypography.body.lineHeight};
  width: 100%;

  box-sizing: border-box;
  clear: both;
  text-align: left;

  /* States */
  &:focus,
  &:focus-within {
    outline: none;
    &:-moz-focusring {
      outline: none;
    }
  }

  &[disabled],
  fieldset[disabled] & {
    cursor: not-allowed;
  }
`;

export const floatingLabelWrapper = (theme: Theme) => css`
  ${controlBase(theme)};
  z-index: 1;
  cursor: pointer;
  border-radius: ${borders.radiusBase};
`;

export const hasIconStyles = css`
  width: 99.5%;
`;

export const requiredAsterisk = (theme: Theme) => css`
  color: ${theme.danger.textBase};
`;

export const rightIcon = {
  base: (theme: Theme) => css`
    transition: all 0.3s ${animations.easeOutCubic};
    animation-duration: ${timing.slow};
    display: block;
    margin-top: -0.4375em;
    pointer-events: none;
    position: absolute;
    top: 50%;
    right: 1em;

    z-index: 4;

    path {
      fill: ${theme.system.textBase};
    }
  `,

  errorStyles: (theme: Theme) => css`
    path {
      fill: ${theme.danger.textBase};
    }
  `,
  disabled: (theme: Theme) => css`
    path {
      fill: ${theme.disabled.text};
    }
  `,
  focused: (theme: Theme) => css`
    path {
      fill: ${theme.text.emphasis};
    }
  `,
};

export const htmlSelect = {
  base: (theme: Theme) => css`
    ${controlBase(theme)};
    color: transparent;
    z-index: 2;
    padding: 1rem;
    top: 0;
    appearance: none;
    border: 1px solid ${theme.system.borderBase};
    border-radius: ${borders.radiusBase};
    position: relative;
    cursor: pointer;

    option {
      color: ${theme.system.textBase};
    }

    &[disabled],
    fieldset[disabled] & {
      background-color: ${theme.system.backgroundMutedHover};
      border-color: ${theme.disabled.border};
      color: ${theme.disabled.text};
      padding-top: 1.5rem;
      padding-bottom: 0.5rem;
      opacity: 1;
    }
  `,
  hovered: (theme: Theme) => css`
    border-color: ${theme.primary.borderHover};
  `,
  filled: (theme: Theme) => css`
    color: ${theme.system.textBase};
    padding-top: 1.5rem;
    padding-bottom: 0.5rem;
  `,
  errorStyles: (theme: Theme) => css`
    border-color: ${theme.danger.textBase};
  `,
  focused: (theme: Theme) => css`
    border-color: ${theme.primary.borderActive};
  `,
};

export const label = {
  base: (theme: Theme) => css`
    transition:
      transform 0.1s cubic-bezier(0.1, 0.55, 0.83, 0.67),
      color 0.3s ease-out;
    font-size: 1rem;
    position: absolute;
    transform-origin: top left;
    transform: translate(16px, 0) scale(1);
    color: ${theme.system.textBase};
    cursor: text;
    pointer-events: none;
    z-index: 3;
  `,
  filled: (theme: Theme) => css`
    color: ${theme.system.textBase};
    transform: translate(16px, -12px) scale(0.85);
  `,
  errorStyles: (theme: Theme) => css`
    color: ${theme.danger.textBase};
  `,
  disabled: (theme: Theme) => css`
    color: ${theme.disabled.text};
    transform: translate(16px, -12px) scale(0.85);
  `,
  focused: (theme: Theme) => css`
    color: ${theme.text.emphasis};
    font-weight: ${theme.aviaryTypography.h5.fontWeight};
  `,
};
